import React, { useState, useEffect } from 'react';
import { User, Mail, School, Calendar, MapPin, Phone, Edit3, Camera, Loader, ChevronDown } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification, showSuccessNotification } from '../../utils/errorHandler';

const LearnerProfile = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadChildren();
    }, []);

    const loadChildren = async () => {
        try {
            setLoading(true);
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            setError(err.friendlyMessage || 'Failed to load children data');
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    if (loading) {
        return (
            <Layout pageTitle="Learner's Profile">
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Profile</h2>
                        <p className="text-gray-600">Please wait while we fetch the profile information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !selectedChild) {
        return (
            <Layout pageTitle="Learner's Profile">
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Profile Available</h2>
                        <p className="text-gray-600 mb-4">
                            {error || 'No child profile found. Please contact your school administrator.'}
                        </p>
                        <button 
                            onClick={loadChildren}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Profile">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    border: 1px solid #e5e7eb;
                    background: white;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .card-parent:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
            `}</style>

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
                {/* Header */}
                <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center space-x-4">
                                <div>
                                    <User className="w-12 h-12 text-white"/>
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        {selectedChild ? `${selectedChild.name}'s Profile` : "Learner's Profile"}
                                    </h1>
                                    <p className="text-green-100">
                                        View and manage profile information
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 appearance-none cursor-pointer pr-10"
                                        disabled={!children.length}
                                    >
                                        {children.length === 0 ? (
                                            <option value="" className="text-gray-900">No children linked</option>
                                        ) : (
                                            children.map(child => (
                                                <option key={child.student_id} value={child.student_id} className="text-gray-900">
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown className="w-4 h-4 text-white absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="max-w-6xl mx-auto px-6 py-8">

                    {/* Profile Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Profile Picture and Basic Info */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="text-center">
                                    <div className="relative inline-block mb-6">
                                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                                            {selectedChild.profile_picture_url ? (
                                                <img 
                                                    src={selectedChild.profile_picture_url} 
                                                    alt={selectedChild.name}
                                                    className="w-32 h-32 rounded-full object-cover"
                                                />
                                            ) : (
                                                selectedChild.name.charAt(0).toUpperCase()
                                            )}
                                        </div>
                                        <button className="absolute bottom-0 right-0 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg transition-colors">
                                            <Camera size={16} />
                                        </button>
                                    </div>
                                    
                                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedChild.name}</h2>
                                    <p className="text-gray-600 mb-4">Grade {selectedChild.grade || 'N/A'}</p>
                                    
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div className="flex items-center justify-center text-blue-600 mb-2">
                                            <School className="w-5 h-5 mr-2" />
                                            <span className="font-medium">School Information</span>
                                        </div>
                                        <p className="text-sm text-gray-700">{selectedChild.school || 'School information not available'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Information */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-2xl font-bold text-gray-800">Personal Information</h3>
                                    <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                                        <Edit3 size={16} />
                                        Edit Profile
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Contact Information */}
                                    <div className="space-y-4">
                                        <h4 className="text-lg font-semibold text-gray-700 border-b border-gray-200 pb-2">
                                            Contact Details
                                        </h4>
                                        
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <Mail className="w-5 h-5 text-blue-500" />
                                            <div>
                                                <p className="text-sm text-gray-600">Email</p>
                                                <p className="font-medium text-gray-800">{selectedChild.email}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <MapPin className="w-5 h-5 text-green-500" />
                                            <div>
                                                <p className="text-sm text-gray-600">Location</p>
                                                <p className="font-medium text-gray-800">
                                                    {selectedChild.state && selectedChild.country 
                                                        ? `${selectedChild.state}, ${selectedChild.country}`
                                                        : 'Location not specified'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Academic Information */}
                                    <div className="space-y-4">
                                        <h4 className="text-lg font-semibold text-gray-700 border-b border-gray-200 pb-2">
                                            Academic Details
                                        </h4>
                                        
                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <School className="w-5 h-5 text-purple-500" />
                                            <div>
                                                <p className="text-sm text-gray-600">School</p>
                                                <p className="font-medium text-gray-800">{selectedChild.school || 'Not specified'}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <Calendar className="w-5 h-5 text-orange-500" />
                                            <div>
                                                <p className="text-sm text-gray-600">Grade Level</p>
                                                <p className="font-medium text-gray-800">Grade {selectedChild.grade || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Information */}
                                <div className="mt-8">
                                    <h4 className="text-lg font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-4">
                                        Additional Information
                                    </h4>
                                    
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="text-2xl font-bold text-blue-600">Active</div>
                                                <div className="text-sm text-gray-600">Account Status</div>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-green-600">Verified</div>
                                                <div className="text-sm text-gray-600">Email Status</div>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-purple-600">Student</div>
                                                <div className="text-sm text-gray-600">Role</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default LearnerProfile;